import React, { useState, useMemo, useEffect, useRef } from 'react'
import { Users, MapPin, ChevronUp, ChevronDown } from 'lucide-react'

const InteractiveWorldMap = ({ locations, onLocationSelect, selectedLocation }) => {
  const chartRef = useRef(null)
  const googleChartRef = useRef(null)
  const lastDataTableRef = useRef(null)
  const [isGoogleChartsLoaded, setIsGoogleChartsLoaded] = useState(false)
  const [markerUpdateTrigger, setMarkerUpdateTrigger] = useState(0)
  const [isLegendCollapsed, setIsLegendCollapsed] = useState(false)
  const resizeDebounceRef = useRef(null)
  const lastSelectedRowRef = useRef(null)
  const [geoCache, setGeoCache] = useState(() => {
    try {
      const raw = localStorage.getItem('geoCache_v1')
      return raw ? JSON.parse(raw) : {}
    } catch { return {} }
  })

  // Built-in city coordinates (lat, lng) for precise positioning
  const cityCoordinates = {
    // US Cities
    'Ashburn, Virginia, United States': [39.0437, -77.4875],
    'Boardman, Oregon, United States': [45.8412, -119.7003],
    'Seattle, Washington, United States': [47.6062, -122.3321],
    'Bartlesville, Oklahoma, United States': [36.7473, -95.9808],
    '(not set), Colorado, United States': [39.5501, -105.7821], // Denver area
    
    // Canadian Cities
    'Edmonton, Alberta, Canada': [53.5461, -113.4938],
    'Calgary, Alberta, Canada': [51.0447, -114.0719],
    'Drayton Valley, Alberta, Canada': [53.2333, -114.9833],
    'Drumheller, Alberta, Canada': [51.4644, -112.7081],
    'Fort McMurray, Alberta, Canada': [56.7267, -111.3790],
    'Medicine Hat, Alberta, Canada': [50.0411, -110.6765],
    'Rimbey, Alberta, Canada': [52.6333, -114.2333],
    'Westlock, Alberta, Canada': [54.1547, -113.8608],
    '(not set), Alberta, Canada': [53.9333, -116.5765], // Alberta center
    'Gibsons, British Columbia, Canada': [49.3956, -123.5056],
    '(not set), British Columbia, Canada': [53.7267, -127.6476], // BC center
    
    // International Cities
    'Riyadh, Riyadh Province, Saudi Arabia': [24.7136, 46.6753],
    'Bangkok, Bangkok, Thailand': [13.7563, 100.5018],
    'Manama, Capital Governorate, Bahrain': [26.2285, 50.5860]
  }

  // Country coordinates for markers (expanded list)
  const countryCoordinates = {
    'United States': [-95.7129, 37.0902],
    'Canada': [-106.3468, 56.1304],
    'United Kingdom': [-3.4360, 55.3781],
    'Germany': [10.4515, 51.1657],
    'France': [2.2137, 46.2276],
    'Japan': [138.2529, 36.2048],
    'Australia': [133.7751, -25.2744],
    'Brazil': [-51.9253, -14.2350],
    'India': [78.9629, 20.5937],
    'China': [104.1954, 35.8617],
    'Mexico': [-102.5528, 23.6345],
    'Spain': [-3.7492, 40.4637],
    'Italy': [12.5674, 41.8719],
    'Netherlands': [5.2913, 52.1326],
    'Sweden': [18.6435, 60.1282],
    'Norway': [8.4689, 60.4720],
    'Denmark': [9.5018, 56.2639],
    'Finland': [25.7482, 61.9241],
    'Russia': [105.3188, 61.5240],
    'South Korea': [127.7669, 35.9078],
    'Argentina': [-63.6167, -38.4161],
    'Chile': [-71.5430, -35.6751],
    'South Africa': [22.9375, -30.5595],
    'Egypt': [30.8025, 26.8206],
    'Turkey': [35.2433, 38.9637],
    'Poland': [19.1343, 51.9194],
    'Ukraine': [31.1656, 48.3794],
    'Thailand': [100.9925, 15.8700],
    'Vietnam': [108.2772, 14.0583],
    'Indonesia': [113.9213, -0.7893],
    'Malaysia': [101.9758, 4.2105],
    'Singapore': [103.8198, 1.3521],
    'Philippines': [121.7740, 12.8797],
    'New Zealand': [174.8860, -40.9006],
    'Belgium': [4.4699, 50.5039],
    'Switzerland': [8.2275, 46.8182],
    'Austria': [14.5501, 47.5162],
    'Portugal': [-8.2245, 39.3999],
    'Greece': [21.8243, 39.0742],
    'Czech Republic': [15.4730, 49.8175],
    'Hungary': [19.5033, 47.1625],
    'Romania': [24.9668, 45.9432],
    'Bulgaria': [25.4858, 42.7339],
    'Croatia': [15.2000, 45.1000],
    'Serbia': [21.0059, 44.0165],
    'Slovakia': [19.6990, 48.6690],
    'Slovenia': [14.9955, 46.1512],
    'Lithuania': [23.8813, 55.1694],
    'Latvia': [24.6032, 56.8796],
    'Estonia': [25.0136, 58.5953],
    'Ireland': [-8.2439, 53.4129],
    'Iceland': [-19.0208, 64.9631],
    'Luxembourg': [6.1296, 49.8153],
    'Malta': [14.3754, 35.9375],
    'Cyprus': [33.4299, 35.1264],
    'Israel': [34.8516, 31.0461],
    'Saudi Arabia': [45.0792, 23.8859],
    'United Arab Emirates': [53.8478, 23.4241],
    'Qatar': [51.1839, 25.3548],
    'Kuwait': [47.4818, 29.3117],
    'Oman': [55.9754, 21.4735],
    'Bahrain': [50.6344, 26.0667],
    'Jordan': [36.2384, 30.5852],
    'Lebanon': [35.8623, 33.8547],
    'Syria': [38.9968, 34.8021],
    'Iraq': [43.6793, 33.2232],
    'Iran': [53.6880, 32.4279],
    'Afghanistan': [67.7090, 33.9391],
    'Pakistan': [69.3451, 30.3753],
    'Bangladesh': [90.3563, 23.6850],
    'Sri Lanka': [80.7718, 7.8731],
    'Myanmar': [95.9560, 21.9162],
    'Cambodia': [104.9910, 12.5657],
    'Laos': [102.4955, 19.8563],
    'Mongolia': [103.8467, 46.8625],
    'North Korea': [127.5101, 40.3399],
    'Taiwan': [120.9605, 23.6978],
    'Hong Kong': [114.1694, 22.3193],
    'Macau': [113.5439, 22.1987]
  }

  // Load Google Charts
  useEffect(() => {
    const loadGoogleCharts = () => {
      if (window.google && window.google.charts) {
        setIsGoogleChartsLoaded(true)
        return
      }

      const script = document.createElement('script')
      script.src = 'https://www.gstatic.com/charts/loader.js'
      script.onload = () => {
        window.google.charts.load('current', {
          packages: ['geochart'],
        })
        window.google.charts.setOnLoadCallback(() => {
          setIsGoogleChartsLoaded(true)
        })
      }
      document.head.appendChild(script)
    }

    loadGoogleCharts()
  }, [])

  // Get marker size based on user count
  const getMarkerSize = (users) => {
    if (users >= 100) return 12
    if (users >= 50) return 10
    if (users >= 20) return 8
    if (users >= 10) return 6
    return 4
  }

  // Get marker color based on user count and type
  const getMarkerColor = (users, isLive = false, isCurrentUser = false) => {
    if (isCurrentUser) return '#ffd700' // gold for current user
    if (isLive) return '#00ff00' // bright green for live users
    if (users >= 100) return '#ef4444' // red
    if (users >= 50) return '#f97316'  // orange
    if (users >= 20) return '#eab308'  // yellow
    if (users >= 10) return '#22c55e'  // green
    return '#3b82f6' // blue
  }

  // Process locations data for Google Charts with markers
  const { chartData, rowIndexMap } = useMemo(() => {
    const data = [['Lat', 'Long', 'Location', 'Users']]
    const indexMap = new Map()
    
    // Add individual location markers (not just countries)
    locations.forEach(location => {
      const country = location.country
      const coordinates = countryCoordinates[country]
      
      if (coordinates) {
        // Priority: 1) Built-in city coords, 2) Cached geocoded coords, 3) Country center
        const key = location.city ? `${location.city}, ${location.region || ''}, ${country}` : country
        const builtIn = cityCoordinates[key]
        const cached = geoCache[key]
        let lat, lng
        
        if (builtIn && Array.isArray(builtIn) && builtIn.length === 2) {
          // Use built-in precise coordinates
          lat = builtIn[0]
          lng = builtIn[1]
        } else if (cached && typeof cached.lat === 'number' && typeof cached.lng === 'number') {
          // Use cached geocoded coordinates
          lat = cached.lat
          lng = cached.lng
        } else {
          // Fall back to country center
          const [lngRaw, latRaw] = coordinates
          lat = latRaw
          lng = lngRaw
        }
        
        // Create a unique identifier for each location
        const locationName = location.city ? `${location.city}, ${location.region}, ${country}` : country
        
        data.push([lat, lng, locationName, location.users])
        // Map exact name -> row index in DataTable (0-based, excluding header)
        indexMap.set(locationName, data.length - 2)
      }
    })

    return { chartData: data, rowIndexMap: indexMap }
  }, [locations, geoCache])

  // Geocode missing city coordinates using Nominatim with simple throttling
  useEffect(() => {
    let cancelled = false
    const toFetch = []
    locations.forEach(loc => {
      if (!loc || !loc.country || !loc.city) return
      const key = `${loc.city}, ${loc.region || ''}, ${loc.country}`
      if (!geoCache[key]) toFetch.push({ key, city: loc.city, region: loc.region, country: loc.country })
    })
    if (toFetch.length === 0) return

    const fetchOne = async (item, delayMs) => {
      await new Promise(r => setTimeout(r, delayMs))
      if (cancelled) return
      try {
        const qParts = [item.city, item.region, item.country].filter(Boolean).join(', ')
        const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${encodeURIComponent(qParts)}`
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } })
        if (!res.ok) return
        const json = await res.json()
        if (Array.isArray(json) && json.length > 0) {
          const first = json[0]
          const lat = parseFloat(first.lat)
          const lng = parseFloat(first.lon)
          if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
            setGeoCache(prev => {
              const next = { ...prev, [item.key]: { lat, lng, ts: Date.now() } }
              try { localStorage.setItem('geoCache_v1', JSON.stringify(next)) } catch {}
              return next
            })
          }
        }
      } catch (e) {
        // silent fail; fallback to country center
      }
    }

    // Throttle: 1 request per 300ms to be polite
    toFetch.slice(0, 20).forEach((item, i) => {
      fetchOne(item, i * 300)
    })

    return () => { cancelled = true }
  }, [locations])

  // Get marker colors for current data state (do NOT depend on selectedLocation)
  const getMarkerColors = useMemo(() => {
    const colors = []
    
    if (!locations || !Array.isArray(locations)) {
      return colors
    }
    
    locations.forEach(location => {
      if (!location || !location.country) {
        return // Skip invalid locations
      }
      
      const coordinates = countryCoordinates[location.country]
      
      if (coordinates) {
        // Check if this is the current user's location (Edmonton, Alberta)
        const isCurrentUser = location.city === 'Edmonton' && location.region === 'Alberta'
        
        // Determine marker color
        if (isCurrentUser) {
          colors.push('#ffd700') // Gold for current user
        } else {
          colors.push(getMarkerColor(location.users || 0, false, false))
        }
      }
    })
    
    return colors
  }, [locations])

  // Process locations data for markers
  const mapMarkers = useMemo(() => {
    const countryData = {}
    
    locations.forEach(location => {
      const country = location.country
      if (!countryData[country]) {
        countryData[country] = {
          country,
          totalUsers: 0,
          totalSessions: 0,
          cities: []
        }
      }
      countryData[country].totalUsers += location.users
      countryData[country].totalSessions += location.sessions
      countryData[country].cities.push({
        city: location.city,
        region: location.region,
        users: location.users,
        sessions: location.sessions
      })
    })

    return Object.values(countryData).map(country => ({
      ...country,
      coordinates: countryCoordinates[country.country] || [0, 0],
      hasCoordinates: !!countryCoordinates[country.country]
    })).filter(country => country.hasCoordinates)
  }, [locations])

  // Draw the chart with built-in markers
  useEffect(() => {
    if (!isGoogleChartsLoaded || !chartRef.current || chartData.length <= 1) return

    const drawChart = () => {
      const data = window.google.visualization.arrayToDataTable(chartData)
      lastDataTableRef.current = data

      const options = {
        displayMode: 'markers',
        backgroundColor: 'transparent',
        datalessRegionColor: '#f3f4f6',
        defaultColor: '#f9fafb',
        width: '100%',
        height: '100%',
        keepAspectRatio: false, // Allow flexible aspect ratio
        markerOpacity: 0.9,
        tooltip: {
          textStyle: {
            color: '#000000',
            fontSize: 12
          },
          showColorCode: false,
          trigger: 'focus'
        },
        legend: 'none',
        // Enable zooming and panning
        magnifyingGlass: {
          enable: true,
          zoomFactor: 7.5
        },
        // Marker styling
        sizeAxis: {
          minValue: 4,
          maxValue: 20,
          minSize: 6,
          maxSize: 16
        },
        // Color axis for custom colors
        colorAxis: {
          colors: getMarkerColors
        }
      }

      const chart = new window.google.visualization.GeoChart(chartRef.current)
      
      // Add click handler
      window.google.visualization.events.addListener(chart, 'select', () => {
        try {
          const selection = chart.getSelection()
          if (selection && selection.length > 0 && selection[0] && typeof selection[0].row === 'number') {
            const selectedRow = selection[0].row
            if (selectedRow >= 0 && selectedRow < data.getNumberOfRows()) {
              const locationName = data.getValue(selectedRow, 2) // Location is in column 2
              if (locationName && typeof locationName === 'string') {
                // Extract country from location name (last part after last comma)
                const countryName = locationName.split(', ').pop()
                const locationToSelect = locations.find(loc => loc.country === countryName)
                if (locationToSelect && onLocationSelect) {
                  onLocationSelect(locationToSelect)
                }
              }
            }
          }
        } catch (error) {
          console.error('Error handling map selection:', error)
          // Clear any invalid selection
          try {
            chart.setSelection([])
          } catch (clearError) {
            console.warn('Could not clear selection:', clearError)
          }
        }
      })

      googleChartRef.current = chart
      try {
        chart.draw(data, options)
        // Re-apply last known selection after draw
        const row = lastSelectedRowRef.current
        if (typeof row === 'number' && row >= 0) {
          const dt = lastDataTableRef.current
          const n = dt && typeof dt.getNumberOfRows === 'function' ? dt.getNumberOfRows() : null
          if (n !== null && row < n) {
            const doSelect = () => {
              try {
                chart.setSelection([{ row }])
              } catch (e) {
                console.warn('Reapply selection failed', e)
              }
            }
            if (typeof window.requestAnimationFrame === 'function') {
              requestAnimationFrame(() => setTimeout(doSelect, 0))
            } else {
              setTimeout(doSelect, 0)
            }
          }
        }
      } catch (drawErr) {
        console.error('Chart draw error', drawErr)
      }
    }

    // Initial draw
    drawChart()

    // Add resize listener to redraw chart when window resizes
    const handleResize = () => {
      if (!googleChartRef.current) return
      if (resizeDebounceRef.current) {
        clearTimeout(resizeDebounceRef.current)
      }
      resizeDebounceRef.current = setTimeout(() => {
        try {
          drawChart()
        } catch (e) {
          console.warn('Resize redraw failed', e)
        }
      }, 150)
    }

    window.addEventListener('resize', handleResize)

    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isGoogleChartsLoaded, chartData, locations])

  // Highlight selected country
  useEffect(() => {
    if (!googleChartRef.current || !selectedLocation || !isGoogleChartsLoaded || !chartData || chartData.length <= 1) return
    
    // Validate selectedLocation has required properties
    if (!selectedLocation.country || typeof selectedLocation.country !== 'string') {
      console.warn('Invalid selectedLocation:', selectedLocation)
      return
    }
    
    try {
      // Build possible keys to handle missing/unknown regions
      const keys = []
      if (selectedLocation.city) {
        const region = selectedLocation.region || ''
        // Full key (city, region, country)
        keys.push(`${selectedLocation.city}, ${region}, ${selectedLocation.country}`)
        // If region is missing, try city + country only
        if (!region || region.toLowerCase() === '(not set)') {
          keys.push(`${selectedLocation.city}, ${selectedLocation.country}`)
        }
      }
      // Country-only fallback
      keys.push(selectedLocation.country)

      // Find first matching key in the map
      let idx = -1
      for (const k of keys) {
        if (rowIndexMap.has(k)) {
          idx = rowIndexMap.get(k) + 1 // +1 for header row
          break
        }
      }
      const locationIndex = idx
      
      if (locationIndex > 0) { // Skip header row
        const rowIndex = locationIndex - 1
        // Validate the row index against the last drawn DataTable
        const dt = lastDataTableRef.current
        const numberOfRows = dt && typeof dt.getNumberOfRows === 'function' ? dt.getNumberOfRows() : null
        if (
          rowIndex >= 0 &&
          numberOfRows !== null &&
          rowIndex < numberOfRows &&
          googleChartRef.current && typeof googleChartRef.current.setSelection === 'function'
        ) {
          // Clear any existing selection first
          try { googleChartRef.current.setSelection([]) } catch {}
          // Defer selection to avoid racing with an internal redraw
          const doSelect = () => {
            try {
              const dt2 = lastDataTableRef.current
              const lat = dt2?.getValue(rowIndex, 0)
              const lng = dt2?.getValue(rowIndex, 1)
              if (typeof lat === 'number' && typeof lng === 'number' && !Number.isNaN(lat) && !Number.isNaN(lng)) {
                googleChartRef.current.setSelection([{ row: rowIndex }])
              } else {
                console.warn('Skip selection due to invalid lat/lng', { rowIndex, lat, lng })
              }
            } catch (e) {
              console.error('Deferred setSelection error', e)
            }
          }
          if (typeof window.requestAnimationFrame === 'function') {
            requestAnimationFrame(() => setTimeout(doSelect, 0))
          } else {
            setTimeout(doSelect, 0)
          }
          // Cache last selected row for redraws
          lastSelectedRowRef.current = rowIndex
        } else {
          console.warn('RowIndex out of bounds for selection', { rowIndex, numberOfRows })
        }
      } else {
        // Clear selection if no matching location found
        if (googleChartRef.current && typeof googleChartRef.current.setSelection === 'function') {
          googleChartRef.current.setSelection([])
        }
        lastSelectedRowRef.current = null
      }
    } catch (error) {
      console.error('Error highlighting selected location:', error)
      // Try to clear selection on error
      try {
        if (googleChartRef.current && typeof googleChartRef.current.setSelection === 'function') {
          googleChartRef.current.setSelection([])
        }
      } catch (clearError) {
        console.warn('Could not clear selection after error:', clearError)
      }
    }
  }, [selectedLocation, chartData, isGoogleChartsLoaded])

  return (
    <div style={{ position: 'relative', width: '100%', height: '400px', marginBottom: '1.5rem' }}>
      {/* Map Container */}
      <div style={{
        width: '100%',
        height: '100%',
        background: 'rgba(255, 255, 255, 0.02)',
        borderRadius: '12px',
        border: '1px solid var(--border-color)',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Loading State */}
        {!isGoogleChartsLoaded && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            color: 'var(--text-color-light)',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <div style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              borderTop: '2px solid #3b82f6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            Loading world map...
          </div>
        )}

        {/* Google Charts Map */}
        <div 
          ref={chartRef}
          style={{
            width: '100%',
            height: '100%',
            opacity: isGoogleChartsLoaded ? 1 : 0,
            transition: 'opacity 0.3s ease'
          }}
        />


        {/* Map Stats Overlay */}
        <div style={{
          position: 'absolute',
          bottom: '1rem',
          left: '1rem',
          background: 'rgba(0, 0, 0, 0.8)',
          padding: '0.75rem',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'white',
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.25rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Users size={14} />
            Global Reach
          </div>
          <div>{chartData.length - 1} countries with users</div>
          <div>{locations.reduce((sum, loc) => sum + loc.users, 0)} total users</div>
        </div>

        {/* Legend */}
        <div style={{
          position: 'absolute',
          top: '1rem',
          right: '1rem',
          background: 'rgba(0, 0, 0, 0.8)',
          borderRadius: '8px',
          fontSize: '12px',
          color: 'white',
          backdropFilter: 'blur(4px)',
          transition: 'all 0.3s ease',
          minWidth: '200px'
        }}>
          {/* Legend Header with Collapse Button */}
          <div 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              padding: '0.75rem',
              cursor: 'pointer',
              borderBottom: isLegendCollapsed ? 'none' : '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={() => setIsLegendCollapsed(!isLegendCollapsed)}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontWeight: '600' }}>
              <MapPin size={14} />
              User Distribution
            </div>
            <button
              style={{
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '2px',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '4px',
                transition: 'background-color 0.2s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              title={isLegendCollapsed ? 'Expand legend' : 'Collapse legend'}
            >
              {isLegendCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            </button>
          </div>

          {/* Legend Content - Collapsible */}
          {!isLegendCollapsed && (
            <div style={{ padding: '0 0.75rem 0.75rem 0.75rem' }}>
              {/* Country Colors */}
              <div style={{ marginBottom: '0.75rem' }}>
                <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '0.25rem' }}>Country Intensity:</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
                  <div style={{ width: '12px', height: '8px', background: 'linear-gradient(to right, #e0f2fe, #1d4ed8)' }} />
                  <span>Light to Dark Blue</span>
                </div>
              </div>

              {/* Marker Dots */}
              <div>
                <div style={{ fontSize: '11px', fontWeight: '500', marginBottom: '0.25rem' }}>User Markers:</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '9px', height: '9px', borderRadius: '50%', background: '#ff6600', boxShadow: '0 0 4px rgba(255, 102, 0, 0.6)' }} />
                    <span>Selected Location</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ffd700' }} />
                    <span>You (Current User)</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#00ff00' }} />
                    <span>Live Users</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444' }} />
                    <span>100+ users</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#f97316' }} />
                    <span>50+ users</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#eab308' }} />
                    <span>20+ users</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#22c55e' }} />
                    <span>10+ users</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <div style={{ width: '3px', height: '3px', borderRadius: '50%', background: '#3b82f6' }} />
                    <span>&lt;10 users</span>
                  </div>
                </div>
              </div>
              
              <div style={{ fontSize: '10px', opacity: 0.8, marginTop: '0.5rem' }}>Click countries or dots to explore</div>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  )
}

export default InteractiveWorldMap


